import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BirthdayFundCertificateDesigner, type CertificateDesignerData } from "./BirthdayFundCertificateDesigner";
import { 
  useGetCertificateTemplate, 
  useCreateCertificateInstanceComplete
} from "@/lib/api/birthdayFundCertificates";
import { GenerateLoadingOrError } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";

export function BirthdayFundCreateInstance() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const templateId = searchParams.get("templateId");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<Partial<CertificateDesignerData> | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const templateRes = useGetCertificateTemplate(templateId);
  const createInstanceComplete = useCreateCertificateInstanceComplete();

  useEffect(() => {
    if (templateId && templateRes.data?.success) {
      const template = templateRes.data.data.template;
      setInitialData(template.template_data as Partial<CertificateDesignerData>);
    } else if (!templateId) {
      setInitialData({});
    }
  }, [templateId, templateRes.data]);

  const loadingOrErrorUi = templateId ? GenerateLoadingOrError([templateRes]) : null;

  if (loadingOrErrorUi) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Create Certificate Instance
        </h1>
        {loadingOrErrorUi}
      </section>
    );
  }

  if (initialData === null) {
    return null;
  }

  const handleCreateInstance = async (_data: CertificateDesignerData, _previewElement: HTMLDivElement) => {
    if (!templateId) {
      setSaveError("No template selected");
      return;
    }

    setSaveError(null);
    setIsCreating(true);

    try {
      const result = await createInstanceComplete({ template_id: templateId });

      if (!result.success) {
        throw new Error(result.message || "Failed to create certificate instance");
      }

      await downloadFromS3(
        result.data.presigned_get_url,
        `birthday-beacon-certificate-${result.data.instance.certificate_id.replace(/#/, "")}.pdf`
      );

      toast({
        title: "Instance Created",
        description: `Certificate ${result.data.instance.certificate_id} has been created successfully.`,
      });

      navigate("/fund/birthday/certificates");
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create instance";
      setSaveError(errorMessage);
      console.error("Create instance error:", err);

      toast({
        title: "Failed to Create Instance",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div>
      {saveError && (
        <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
          Failed to create instance: {saveError}
        </div>
      )}

      <BirthdayFundCertificateDesigner
        initialData={initialData}
        onCreateInstance={handleCreateInstance}
        isProcessing={isCreating}
      />
    </div>
  );
}

export default BirthdayFundCreateInstance;
